import type { ReportTrendChart } from "../../reports/reportRevenue";
import { defaultFetchHeaders } from "../../../utils";
import { errorMessageFromResponse } from "../../../apiError";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const reportsBase = `${backend}/reports`;

type ReportChartEntry = {
  label: string;
  grossCents: number;
  refundsCents: number;
  commissionCents: number;
  netCents: number;
};

const serializeChartData = (entries?: ReportChartEntry[]) =>
  entries?.map((entry) => ({
    label: entry.label,
    gross_cents: entry.grossCents,
    refunds_cents: entry.refundsCents,
    commission_cents: entry.commissionCents,
    net_cents: entry.netCents,
  }));

export type TaskReportFormat = "pdf" | "xlsx";
export type TaskReportTemplate = "task_report" | "commission_report";

function parseFilename(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  if (match && match[1]) return decodeURIComponent(match[1]);
  return fallback;
}

export async function downloadTaskReport(params: {
  taskId: string;
  format: TaskReportFormat;
  template?: TaskReportTemplate;
  fields?: string[];
  fieldValues?: Record<string, string>;
  includeTransactions?: boolean;
  scope?: "growth";
}): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(params.scope === "growth" ? `${backend}/growth/reports/task` : `${reportsBase}/task`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      task_id: params.taskId,
      format: params.format,
      template: params.template ?? "task_report",
      fields: params.fields,
      field_values: params.fieldValues,
      include_transactions: params.includeTransactions,
    }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to download report"),
    );
  }
  const blob = await res.blob();
  const filename = parseFilename(
    res.headers.get("content-disposition"),
    `task-report.${params.format}`,
  );
  return { blob, filename };
}

export async function downloadExpenseReport(params: {
  expenseId: string;
  format: TaskReportFormat;
  reportGeneratedAt?: string;
}): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${reportsBase}/expense`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      expense_id: params.expenseId,
      format: params.format,
      report_generated_at: params.reportGeneratedAt,
    }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to download expense report"),
    );
  }
  const blob = await res.blob();
  const filename = parseFilename(
    res.headers.get("content-disposition"),
    `expense-report.${params.format}`,
  );
  return { blob, filename };
}

export async function downloadCollectionHistoryReport(params: {
  format: TaskReportFormat;
  rangeStart?: string;
  rangeEnd?: string;
  reportGeneratedAt?: string;
  machineId?: string;
  locationId?: string;
  rows: string[][];
  totals:
    | { gross: string; refunds: string; commission: string; net: string }
    | { commission: string };
  reportType?: "collection_history" | "commission_history";
  groupByLocation?: boolean;
}): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${reportsBase}/collection-history`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      format: params.format,
      range_start: params.rangeStart,
      range_end: params.rangeEnd,
      report_generated_at: params.reportGeneratedAt,
      machine_id: params.machineId,
      location_id: params.locationId,
      rows: params.rows,
      totals: params.totals,
      report_type: params.reportType ?? "collection_history",
      group_by_location: params.groupByLocation ?? false,
    }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to download report"),
    );
  }
  const blob = await res.blob();
  const filename = parseFilename(
    res.headers.get("content-disposition"),
    `collection-history.${params.format}`,
  );
  return { blob, filename };
}

export async function downloadTableReport(params: {
  format: TaskReportFormat;
  rangeStart?: string;
  rangeEnd?: string;
  reportGeneratedAt?: string;
  reportTitle?: string;
  headers: string[];
  rows: string[][];
  chartData?: ReportChartEntry[];
  reportKind?: "locations" | "profit-loss";
  trendChart?: ReportTrendChart;
}): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${reportsBase}/${params.reportKind || "locations"}`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      format: params.format,
      range_start: params.rangeStart,
      range_end: params.rangeEnd,
      report_generated_at: params.reportGeneratedAt,
      report_title: params.reportTitle,
      headers: params.headers,
      rows: params.rows,
      chart_data: serializeChartData(params.chartData),
      ...(params.trendChart ? { trend_chart: {
        labels: params.trendChart.labels,
        minimum_cents: params.trendChart.minimumCents,
        maximum_cents: params.trendChart.maximumCents,
        tick_step_cents: params.trendChart.tickStepCents,
        series: params.trendChart.series.map((series) => ({ label: series.label, kind: series.kind, color: series.color, values_cents: series.valuesCents, dashed: series.dashed })),
      } } : {}),
    }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to download report"),
    );
  }
  const blob = await res.blob();
  const filename = parseFilename(
    res.headers.get("content-disposition"),
    `location-reports.${params.format}`,
  );
  return { blob, filename };
}

export async function downloadMachinesReport(params: {
  format: TaskReportFormat;
  rangeStart?: string;
  rangeEnd?: string;
  reportGeneratedAt?: string;
  reportTitle?: string;
  headers: string[];
  rows: string[][];
  chartData?: ReportChartEntry[];
}): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${reportsBase}/machines`, {
    method: "POST",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      format: params.format,
      range_start: params.rangeStart,
      range_end: params.rangeEnd,
      report_generated_at: params.reportGeneratedAt,
      report_title: params.reportTitle,
      headers: params.headers,
      rows: params.rows,
      chart_data: serializeChartData(params.chartData),
    }),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to download report"),
    );
  }
  const blob = await res.blob();
  const filename = parseFilename(
    res.headers.get("content-disposition"),
    `machine-reports.${params.format}`,
  );
  return { blob, filename };
}

export const downloadLocationsReport = downloadTableReport;
